import { INTEGRATION_TYPE } from '@steerfi/shared';
import { IntegrationProfileConfigSchema } from '@steerfi/shared/types/jsonb/integration-profile.schemas';
import type { IntegrationProfileConfig } from '@steerfi/shared/types/jsonb/integration-profile.types';
import type React from 'react';
import { useEffect, useState } from 'react';
import { JsonEditor } from '@/components/json-editor';
import { ApiDeliveryFields } from './api-delivery-fields';
import { SftpAcquisitionFields } from './sftp-acquisition-fields';
import { SftpDeliveryFields } from './sftp-delivery-fields';

interface ConfigJsonEditorProps {
  integrationTypeId: number;
  value: IntegrationProfileConfig | null | undefined;
  onChange: (v: IntegrationProfileConfig | null) => void;
  onConfigError?: (hasError: boolean) => void;
  providerPartyId?: string;
  mode?: 'create' | 'edit';
  disabled?: boolean;
}

export const ConfigJsonEditor: React.FC<ConfigJsonEditorProps> = ({
  integrationTypeId,
  value,
  onChange,
  onConfigError,
  providerPartyId,
  disabled,
}) => {
  const [apiDuplicate, setApiDuplicate] = useState(false);

  useEffect(() => {
    // API reports its own blank or duplicate row error. Clearing on every config change
    // would drop that flag the moment a header value is edited.
    if (integrationTypeId === INTEGRATION_TYPE.API) {
      onConfigError?.(apiDuplicate);
      return;
    }
    if (integrationTypeId === INTEGRATION_TYPE.SFTP || value !== null) {
      onConfigError?.(false);
    }
  }, [integrationTypeId, value, onConfigError, apiDuplicate]);

  if (integrationTypeId === INTEGRATION_TYPE.SFTP) {
    return (
      <div className="space-y-3">
        <SftpDeliveryFields
          value={value?.delivery?.sftp}
          disabled={disabled ?? false}
          onChange={(sftp) =>
            onChange({ ...(value ?? {}), delivery: { ...(value?.delivery ?? {}), sftp } })
          }
        />
        <SftpAcquisitionFields
          value={value?.acquisition?.sftp}
          disabled={disabled ?? false}
          {...(providerPartyId ? { partyId: providerPartyId } : {})}
          onChange={(sftp) =>
            onChange({ ...(value ?? {}), acquisition: { ...(value?.acquisition ?? {}), sftp } })
          }
        />
      </div>
    );
  }

  if (integrationTypeId === INTEGRATION_TYPE.API) {
    return (
      <ApiDeliveryFields
        value={value?.delivery?.api}
        disabled={disabled ?? false}
        onDuplicateChange={setApiDuplicate}
        onChange={(api) =>
          onChange({ ...(value ?? {}), delivery: { ...(value?.delivery ?? {}), api } })
        }
      />
    );
  }

  return (
    <JsonEditor
      helperText="Raw JSON configuration validated against the integration profile schema."
      schema={IntegrationProfileConfigSchema}
      value={value}
      onChange={(v) => onChange((v as IntegrationProfileConfig | null) ?? null)}
      {...(onConfigError ? { onErrorsChange: onConfigError } : {})}
      required
      disabled={disabled ?? false}
    />
  );
};
